import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ClickableAvatar } from "../components/ClickableAvatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, LogIn, LogOut,
  UserCheck, Phone, Search, Filter, X, List, Eye, Users, Briefcase, Fingerprint,
  ChevronDown, ChevronUp, Layers, History, Clock,
} from "lucide-react";
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  addMonths, subMonths, isToday,
} from "date-fns";

const DAYS_OF_WEEK = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const EMPTY_FILTERS = { name: "", phone: "", memberId: "", dateFrom: "", dateTo: "" };

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

function formatTime(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function calcDuration(checkIn, checkOut) {
  if (!checkIn || !checkOut) return null;
  const diff = Math.round((new Date(checkOut) - new Date(checkIn)) / 60000);
  if (diff < 60) return `${diff}m`;
  return `${Math.floor(diff / 60)}h ${diff % 60}m`;
}

// ── Quick Check-in Dialog (Dual Mode: Members & Staff) ────────────────────────
function QuickCheckinDialog({ open, onOpenChange, branchId, checkedInIds, onCheckin, onCheckout, attendanceType = "members" }) {
  const [search, setSearch] = useState("");
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentBranch } = useAuth();
  const isStaff = attendanceType === "staff";

  useEffect(() => {
    if (!open) { setSearch(""); setPeople([]); return; }
    fetchPeople("");
  }, [open, attendanceType]);

  useEffect(() => {
    const t = setTimeout(() => fetchPeople(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPeople = async (q) => {
    setLoading(true);
    try {
      if (isStaff) {
        const params = new URLSearchParams();
        params.append("limit", "50");
        if (q) params.append("search", q);
        if (currentBranch?.id) params.append("branchId", currentBranch.id);
        const res = await api.get(`/staff?${params.toString()}`);
        setPeople(res.data.staff || []);
      } else {
        const params = new URLSearchParams();
        params.append("status", "Active");
        params.append("limit", "50");
        if (q) params.append("name", q);
        if (currentBranch?.id) params.append("branchId", currentBranch.id);
        const res = await api.get(`/members?${params.toString()}`);
        let list = res.data.members || [];
        if (q && !list.length) {
          const params2 = new URLSearchParams();
          params2.append("status", "Active");
          params2.append("limit", "50");
          params2.append("phone", q);
          if (currentBranch?.id) params2.append("branchId", currentBranch.id);
          const res2 = await api.get(`/members?${params2.toString()}`);
          list = res2.data.members || [];
        }
        setPeople(list);
      }
    } catch { }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card border-border" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-primary" />
            {isStaff ? "Staff Check-in / Check-out" : "Quick Check-in / Check-out"}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {isStaff ? "Search by staff name, phone, or email" : "Search by name, phone, or member ID"}
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            placeholder={isStaff ? "Staff name, phone, or email..." : "Name, phone, or member ID..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="checkin-search"
          />
        </div>

        {/* People list */}
        <ScrollArea className="max-h-[50vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
            </div>
          ) : people.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              {isStaff ? "No staff members found" : "No active members found"}
            </p>
          ) : (
            <div className="space-y-1 pr-1">
              {people.map((m) => {
                const isIn = checkedInIds.has(m.id);
                return (
                  <div
                    key={m.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                    data-testid={`checkin-person-${m.id}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <ClickableAvatar
                        src={m.photo}
                        name={m.name}
                        className="h-8 w-8 shrink-0"
                        fallbackClassName="bg-primary/10 text-primary text-xs font-bold"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-medium truncate leading-tight">
                            {!isStaff && m.memberNumber && (
                              <span className="text-muted-foreground text-[10px] font-mono mr-1">#{m.memberNumber}</span>
                            )}
                            {m.name}
                          </p>
                          {isStaff && m.role && (
                            <Badge variant="outline" className="text-[9px] uppercase font-mono py-0 h-4">{m.role}</Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground font-mono flex items-center gap-1">
                          {m.phone ? <><Phone className="h-2.5 w-2.5" />{m.phone}</> : m.email}
                        </p>
                      </div>
                    </div>
                    {isIn ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm text-red-400 border-red-400/30 shrink-0 h-7 text-xs"
                        onClick={() => onCheckout(m.id)}
                        data-testid={`checkout-btn-${m.id}`}
                      >
                        <LogOut className="h-3 w-3 mr-1" />Out
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="rounded-sm shrink-0 h-7 text-xs"
                        onClick={() => onCheckin(m.id)}
                        data-testid={`checkin-btn-${m.id}`}
                      >
                        <LogIn className="h-3 w-3 mr-1" />In
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ── Filters Drawer ─────────────────────────────────────────────────────────────
function FiltersDrawer({ open, onOpenChange, filters, onChange, onClear, attendanceType = "members" }) {
  const isDesktop = useIsDesktop();
  const [draft, setDraft] = useState(filters);
  const isStaff = attendanceType === "staff";

  useEffect(() => { if (open) setDraft(filters); }, [open, filters]);

  const set = (key, val) => setDraft(prev => ({ ...prev, [key]: val }));
  const draftCount = Object.values(draft).filter(v => !!v).length;
  const handleApply = () => { onChange(draft); onOpenChange(false); };
  const handleClear = () => { setDraft(EMPTY_FILTERS); onClear(); onOpenChange(false); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isDesktop ? "right" : "bottom"}
        className={isDesktop
          ? "w-[380px] flex flex-col p-0 bg-card border-border"
          : "rounded-t-2xl flex flex-col p-0 max-h-[88vh] bg-card border-border"
        }
      >
        <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
              <Filter className="h-4 w-4" /> Filters
              {draftCount > 0 && (
                <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">{draftCount} active</Badge>
              )}
            </SheetTitle>
            {draftCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClear} className="text-muted-foreground text-xs h-7">
                <X className="h-3.5 w-3.5 mr-1" />Clear all
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {isStaff ? "Staff Name" : "Member Name"}
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Name..." value={draft.name} onChange={e => set("name", e.target.value)} className="pl-9" data-testid="filter-name" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Phone</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Phone..." value={draft.phone} onChange={e => set("phone", e.target.value)} className="pl-9" data-testid="filter-phone" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {isStaff ? "Staff ID" : "Member ID / #"}
            </Label>
            <Input placeholder={isStaff ? "Staff ID..." : "Member number..."} value={draft.memberId} onChange={e => set("memberId", e.target.value)} data-testid="filter-member-id" />
          </div>

          <Separator />

          <div className="space-y-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Date Range</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" value={draft.dateFrom} onChange={e => set("dateFrom", e.target.value)} data-testid="filter-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" value={draft.dateTo} onChange={e => set("dateTo", e.target.value)} data-testid="filter-date-to" />
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-card shrink-0">
          <Button className="w-full font-heading uppercase tracking-wide text-xs" onClick={handleApply} data-testid="apply-filters-btn">
            <Search className="h-4 w-4 mr-2" />Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
const parseMonthParam = (param) => {
  if (!param) return null;
  const parts = param.split("-");
  if (parts.length === 2) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
      return new Date(year, month - 1, 1);
    }
  }
  return null;
};

export default function Attendance() {
  const { currentBranch } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Mode Switcher: Members vs Staff ──
  const attendanceType = searchParams.get("type") === "staff" ? "staff" : "members";
  const isStaff = attendanceType === "staff";

  const handleSwitchType = (type) => {
    const next = new URLSearchParams(searchParams);
    next.set("type", type);
    setSearchParams(next);
    setSelectedDay(new Date());
    setDayRecords([]);
    setListPage(1);
  };

  const activeTab = searchParams.get("tab") || "calendar";
  const setActiveTab = (val) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", val);
    setSearchParams(next);
  };

  // ── Today count (header badge) ──
  const [todayCount, setTodayCount] = useState(0);
  const [checkedInIds, setCheckedInIds] = useState(new Set());
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);

  // ── Calendar state ──
  const monthStr = useMemo(() => {
    const m = searchParams.get("month");
    if (m && parseMonthParam(m)) return m;
    return format(new Date(), "yyyy-MM");
  }, [searchParams]);
  const currentMonth = useMemo(() => parseMonthParam(monthStr) || new Date(), [monthStr]);
  const [calLoading, setCalLoading] = useState(false);
  const [calendarData, setCalendarData] = useState({});
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [dayLoading, setDayLoading] = useState(false);
  const [dayRecords, setDayRecords] = useState([]);
  const [expandedDayRecordIds, setExpandedDayRecordIds] = useState(new Set());

  // ── List state ──
  const [groupByDay, setGroupByDay] = useState(true);
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const [listLoading, setListLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(20);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  const toggleRowExpanded = (id) => {
    setExpandedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleDayRecordExpanded = (id) => {
    setExpandedDayRecordIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filterCount = Object.values(filters).filter(v => !!v).length;

  // ── Fetch today's attendance for badge + checked-in set ──
  const fetchToday = useCallback(async () => {
    try {
      const bq = currentBranch ? `?branchId=${currentBranch.id}` : "";
      const endpoint = isStaff ? `/attendance/staff/today${bq}` : `/attendance/today${bq}`;
      const res = await api.get(endpoint);
      const records = res.data.attendance || [];
      setTodayCount(res.data.count || 0);
      setCheckedInIds(new Set(records.filter(a => a.isActive || !a.checkOut).map(a => isStaff ? (a.staffId || a.entityId) : (a.memberId || a.entityId))));
    } catch { }
  }, [currentBranch, isStaff]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  // ── Calendar tab: fetch monthly summary ──
  const fetchCalendar = useCallback(async () => {
    setCalLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("month", monthStr);
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const endpoint = isStaff ? `/attendance/staff/calendar` : `/attendance/calendar`;
      const res = await api.get(`${endpoint}?${params.toString()}`);
      setCalendarData(res.data.calendar || {});
    } catch (err) { console.error(err); }
    finally { setCalLoading(false); }
  }, [monthStr, currentBranch, isStaff]);

  useEffect(() => {
    if (activeTab === "calendar") fetchCalendar();
  }, [fetchCalendar, activeTab]);

  // ── Day click: lazy fetch that day's records ──
  const fetchDayRecords = useCallback(async (dateStr) => {
    setDayLoading(true);
    setDayRecords([]);
    try {
      const params = new URLSearchParams();
      params.append("dateFrom", dateStr);
      params.append("dateTo", dateStr);
      params.append("pageSize", "200");
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const endpoint = isStaff ? `/attendance/staff/all` : `/attendance/all`;
      const res = await api.get(`${endpoint}?${params.toString()}`);
      setDayRecords(res.data.attendance || []);
    } catch { }
    finally { setDayLoading(false); }
  }, [currentBranch, isStaff]);

  // Fetch day records whenever selectedDay changes or calendar tab is active
  useEffect(() => {
    if (activeTab === "calendar" && selectedDay) {
      fetchDayRecords(format(selectedDay, "yyyy-MM-dd"));
    }
  }, [selectedDay, activeTab, fetchDayRecords]);

  const handleDayClick = (day) => {
    setSelectedDay(day);
    const next = new URLSearchParams(searchParams);
    next.set("month", format(day, "yyyy-MM"));
    setSearchParams(next);
  };

  const handleMonthChange = (newMonth) => {
    const next = new URLSearchParams(searchParams);
    next.set("month", format(newMonth, "yyyy-MM"));
    setSearchParams(next);
    setSelectedDay(null);
    setDayRecords([]);
  };

  // ── List tab: fetch paginated records ──
  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", listPage);
      params.append("pageSize", listPageSize);
      params.append("groupByDay", groupByDay ? "true" : "false");
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      if (filters.name) params.append("name", filters.name);
      if (filters.phone) params.append("phone", filters.phone);
      if (filters.memberId) {
        if (isStaff) params.append("staffId", filters.memberId);
        else params.append("memberId", filters.memberId);
      }
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      const endpoint = isStaff ? `/attendance/staff/all` : `/attendance/all`;
      const res = await api.get(`${endpoint}?${params.toString()}`);
      setListData(res.data.attendance || []);
      setListTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setListLoading(false); }
  }, [listPage, listPageSize, filters, currentBranch, isStaff, groupByDay]);

  useEffect(() => { setListPage(1); }, [filters, listPageSize, currentBranch, isStaff, groupByDay]);

  useEffect(() => {
    if (activeTab === "list") fetchList();
  }, [fetchList, activeTab]);

  // ── Check-in / Check-out ──
  const handleCheckin = async (id) => {
    try {
      if (isStaff) {
        await api.post("/attendance/staff/checkin", { staffId: id, branchId: currentBranch?.id || "" });
      } else {
        await api.post("/attendance/checkin", { memberId: id, branchId: currentBranch?.id || "" });
      }
      toast.success("Checked in");
      fetchToday();
      if (activeTab === "calendar") { fetchCalendar(); if (selectedDay) fetchDayRecords(format(selectedDay, "yyyy-MM-dd")); }
      if (activeTab === "list") fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleCheckout = async (id) => {
    try {
      if (isStaff) {
        await api.post("/attendance/staff/checkout", { staffId: id, branchId: currentBranch?.id || "" });
      } else {
        await api.post("/attendance/checkout", { memberId: id, branchId: currentBranch?.id || "" });
      }
      toast.success("Checked out");
      fetchToday();
      if (activeTab === "calendar") { fetchCalendar(); if (selectedDay) fetchDayRecords(format(selectedDay, "yyyy-MM-dd")); }
      if (activeTab === "list") fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const totalListPages = Math.max(1, Math.ceil(listTotal / listPageSize));

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-1.5 mt-1 md:space-y-3" data-testid="attendance-page">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-lg md:text-3xl font-black font-heading tracking-tight uppercase">Attendance</h1>

            {/* Top switcher: Members vs Staff */}
            <div className="flex items-center bg-secondary/70 p-1 rounded-lg border border-border">
              <button
                type="button"
                onClick={() => handleSwitchType("members")}
                className={`px-3 py-1 rounded-md text-xs font-heading uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                  !isStaff
                    ? "bg-primary text-primary-foreground shadow-sm font-bold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="attendance-switch-members"
              >
                <Users className="h-3.5 w-3.5" />
                Members
              </button>
              <button
                type="button"
                onClick={() => handleSwitchType("staff")}
                className={`px-3 py-1 rounded-md text-xs font-heading uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                  isStaff
                    ? "bg-primary text-primary-foreground shadow-sm font-bold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="attendance-switch-staff"
              >
                <Briefcase className="h-3.5 w-3.5" />
                Staff
              </button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground tracking-tight mt-0.5">
            {isStaff ? "Track staff duty shifts, working hours, and biometric punches" : "Track member check-ins, check-outs, and biometric punches"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-primary/20 text-primary border-0 rounded-sm text-sm hidden sm:flex items-center gap-1">
            <UserCheck className="h-4 w-4" />{todayCount} today
          </Badge>
          <Button
            size="sm"
            className="font-heading uppercase tracking-wide text-xs"
            onClick={() => setShowCheckinDialog(true)}
            data-testid="quick-checkin-btn"
          >
            <UserCheck className="h-4 w-4 mr-1" />
            {isStaff ? "Staff Check-in" : "Quick Check-in"}
          </Button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 space-y-2 md:space-y-3">
        <div className="flex items-center justify-between gap-2">
          <TabsList className="bg-secondary/50 p-1 justify-start">
            <TabsTrigger value="calendar" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-calendar">
              <CalendarIcon className="h-3.5 w-3.5 mr-2" />Calendar
            </TabsTrigger>
            <TabsTrigger value="list" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-list">
              <List className="h-3.5 w-3.5 mr-2" />All Attendance
            </TabsTrigger>
          </TabsList>
          {activeTab === "list" && (
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-secondary/60 rounded-md p-0.5 border border-border">
                <button
                  type="button"
                  onClick={() => setGroupByDay(true)}
                  className={`px-2.5 py-1 rounded text-[11px] font-heading uppercase tracking-wider transition-all flex items-center gap-1 ${
                    groupByDay
                      ? "bg-primary text-primary-foreground font-bold shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="toggle-grouped-day"
                >
                  <Layers className="h-3 w-3" />
                  Daily Summary
                </button>
                <button
                  type="button"
                  onClick={() => setGroupByDay(false)}
                  className={`px-2.5 py-1 rounded text-[11px] font-heading uppercase tracking-wider transition-all flex items-center gap-1 ${
                    !groupByDay
                      ? "bg-primary text-primary-foreground font-bold shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="toggle-raw-punches"
                >
                  <History className="h-3 w-3" />
                  All Punches
                </button>
              </div>
              <Button
                variant={filterCount > 0 ? "secondary" : "outline"}
                size="sm"
                onClick={() => setShowFilters(true)}
                className="font-heading uppercase tracking-wide text-xs"
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4 mr-1" />Filters
                {filterCount > 0 && (
                  <Badge className="ml-1 bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px]">{filterCount}</Badge>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* ══ CALENDAR TAB ══════════════════════════════════════════════════════ */}
        <TabsContent value="calendar" className="flex-1 mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-start">

            {/* Left: Calendar Grid */}
            <Card className="lg:col-span-5 bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 py-2">
                <CardTitle className="text-sm font-heading uppercase tracking-wide">Calendar</CardTitle>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => handleMonthChange(subMonths(currentMonth, 1))} data-testid="prev-month-btn">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[110px] text-center">{format(currentMonth, "MMMM yyyy")}</span>
                  <Button variant="ghost" size="icon" onClick={() => handleMonthChange(addMonths(currentMonth, 1))} data-testid="next-month-btn">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-1">
                {calLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                  </div>
                ) : (
                  <>
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {DAYS_OF_WEEK.map(d => (
                        <div key={d.key} className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground py-2">{d.label}</div>
                      ))}
                    </div>
                    {/* Day cells */}
                    <div className="grid grid-cols-7 gap-0.5">
                      {eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) }).map((day, idx) => {
                        const dayOfWeek = day.getDay();
                        const isFirstDay = idx === 0;
                        const dateStr = format(day, "yyyy-MM-dd");
                        const dayData = calendarData[dateStr];
                        const hasData = dayData && (dayData.checkins > 0 || dayData.checkouts > 0);
                        const isSelected = selectedDay && format(selectedDay, "yyyy-MM-dd") === dateStr;
                        const todayFlag = isToday(day);

                        return (
                          <div
                            key={day.toISOString()}
                            style={{ gridColumn: isFirstDay ? dayOfWeek + 1 : undefined }}
                            onClick={() => handleDayClick(day)}
                            className={`min-h-[60px] sm:min-h-[72px] p-1 sm:p-1.5 rounded-sm border cursor-pointer transition-all ${todayFlag
                              ? "border-primary bg-primary/5 shadow-[0_0_10px_rgba(var(--primary),0.1)]"
                              : isSelected
                                ? "border-primary bg-primary/5"
                                : hasData
                                  ? "border-border bg-secondary/30 hover:bg-secondary/50"
                                  : "border-border/50 bg-background hover:bg-secondary/20"
                              }`}
                            data-testid={`calendar-day-${dateStr}`}
                          >
                            <div className={`text-[10px] font-medium mb-1 ${todayFlag ? "text-primary" : ""}`}>
                              {format(day, "d")}
                            </div>
                            {hasData && (
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-0.5">
                                  <LogIn className="h-2.5 w-2.5 text-emerald-500 shrink-0" />
                                  <span className="text-[9px] font-bold font-mono text-emerald-500">{dayData.checkins}</span>
                                </div>
                                <div className="flex items-center gap-0.5">
                                  <LogOut className="h-2.5 w-2.5 text-amber-500 shrink-0" />
                                  <span className="text-[9px] font-bold font-mono text-amber-500">{dayData.checkouts}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-border">
                      <div className="flex items-center gap-1">
                        <LogIn className="h-3 w-3 text-emerald-500" />
                        <span className="text-[10px] text-muted-foreground">Check-ins</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <LogOut className="h-3 w-3 text-amber-500" />
                        <span className="text-[10px] text-muted-foreground">Check-outs</span>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Right: Day Detail Panel */}
            <Card className="lg:col-span-7 bg-card border-border flex flex-col h-full min-h-[400px] lg:h-[calc(100vh-215px)]">
              <CardHeader className="px-3 py-2 border-b border-border shrink-0">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-primary" />
                  <CardTitle className="text-sm font-heading uppercase tracking-wide">
                    {selectedDay ? format(selectedDay, "EEEE, MMM d, yyyy") : "Select a Day"}
                  </CardTitle>
                  {selectedDay && dayRecords.length > 0 && (
                    <Badge className="ml-auto bg-primary/20 text-primary border-0 text-xs">{dayRecords.length} records</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-0 scroll-smooth">
                {!selectedDay ? (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                    <CalendarIcon className="h-10 w-10 mb-3" />
                    <p className="text-sm font-medium">Select a day from the calendar</p>
                    <p className="text-xs mt-1">to view attendance records</p>
                  </div>
                ) : dayLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                  </div>
                ) : dayRecords.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                    <UserCheck className="h-10 w-10 mb-3" />
                    <p className="text-sm">No attendance records for this day</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {/* Summary bar */}
                    <div className="px-3 py-2 bg-secondary/20 flex items-center justify-between gap-2 text-xs font-mono">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-emerald-500 font-medium">
                          <LogIn className="h-3 w-3" />
                          {new Set(dayRecords.map(r => isStaff ? r.staffId : r.memberId)).size} {isStaff ? "staff" : "members"}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Layers className="h-3 w-3 text-primary" />
                          {dayRecords.reduce((acc, r) => acc + (r.sessionCount || 1), 0)} sessions
                        </span>
                      </div>
                      {dayRecords.some(r => r.isActive || !r.checkOut) && (
                        <span className="flex items-center gap-1 text-emerald-400">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          {dayRecords.filter(r => r.isActive || !r.checkOut).length} inside
                        </span>
                      )}
                    </div>
                    {dayRecords.map((r) => {
                      const duration = r.totalDuration || r.duration || calcDuration(r.firstCheckIn || r.checkIn, r.lastCheckOut || r.checkOut);
                      const personName = isStaff ? (r.staff?.name || r.staffName || "Staff") : (r.member?.name || "Unknown");
                      const personPhoto = isStaff ? r.staff?.photo : r.member?.photo;
                      const personSubtext = isStaff ? (r.staff?.phone || r.staffPhone || r.staffEmail || "") : r.member?.phone;
                      const isBiometric = r.source?.startsWith("biometric_") || r.source === "biometric";
                      const hasMultiSessions = r.sessions && r.sessions.length > 1;
                      const isExpanded = expandedDayRecordIds.has(r.id);

                      return (
                        <div key={r.id} className="p-3 hover:bg-secondary/10 transition-colors">
                          <div className="flex items-center gap-3">
                            <ClickableAvatar
                              src={personPhoto}
                              name={personName}
                              className="h-8 w-8 shrink-0"
                              fallbackClassName="bg-primary/10 text-primary text-xs font-bold"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-sm font-medium truncate leading-tight">
                                  {!isStaff && r.member?.memberNumber && (
                                    <span className="text-muted-foreground text-[10px] font-mono mr-1">#{r.member?.memberNumber}</span>
                                  )}
                                  {personName}
                                </p>
                                {isBiometric && (
                                  <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[9px] font-mono py-0 h-4 flex items-center gap-0.5">
                                    <Fingerprint className="h-2.5 w-2.5" />Bio
                                  </Badge>
                                )}
                                {!isStaff && (r.member?.id || r.memberId) && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 text-muted-foreground hover:text-primary shrink-0"
                                    onClick={() => navigate(`/members/${r.member?.id || r.memberId}`)}
                                    title="View Profile"
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                )}
                                {isStaff && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 text-muted-foreground hover:text-primary shrink-0"
                                    onClick={() => navigate(`/staff`)}
                                    title="View Staff"
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                              <p className="text-[11px] text-muted-foreground font-mono">{personSubtext}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 text-[11px] font-mono">
                              <span className="text-emerald-500">{formatTime(r.firstCheckIn || r.checkIn)}</span>
                              <span className="text-muted-foreground">→</span>
                              {r.lastCheckOut || r.checkOut
                                ? <span className="text-amber-500">{formatTime(r.lastCheckOut || r.checkOut)}</span>
                                : <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px]">Active</Badge>
                              }
                              {duration && (
                                <Badge className="bg-secondary text-muted-foreground border-0 text-[10px] font-mono">{duration}</Badge>
                              )}
                              {hasMultiSessions && (
                                <button
                                  type="button"
                                  onClick={() => toggleDayRecordExpanded(r.id)}
                                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-mono transition-colors"
                                  title="View sessions"
                                >
                                  <Layers className="h-2.5 w-2.5" />
                                  {r.sessionCount || r.sessions.length}
                                  {isExpanded ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Expanded Sessions Timeline */}
                          {hasMultiSessions && isExpanded && (
                            <div className="mt-2.5 ml-11 pr-1">
                              <div className="bg-secondary/20 rounded-md p-2 border border-border/50 space-y-1">
                                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center justify-between mb-1">
                                  <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-primary" />Daily Sessions</span>
                                  <span>Total: <strong className="text-foreground">{duration}</strong></span>
                                </div>
                                {r.sessions.map((s, sIdx) => (
                                  <div key={s.id || sIdx} className="flex items-center justify-between text-[11px] font-mono py-1 px-1.5 rounded bg-card/60 border border-border/20">
                                    <span className="text-muted-foreground text-[10px]">Session #{sIdx + 1}</span>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-emerald-500">{formatTime(s.checkIn)}</span>
                                      <span className="text-muted-foreground">→</span>
                                      {s.checkOut ? <span className="text-amber-500">{formatTime(s.checkOut)}</span> : <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[9px] py-0">Active</Badge>}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px] text-muted-foreground">{s.duration || "—"}</span>
                                      {s.source === "biometric" && (
                                        <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[8px] font-mono py-0">Bio</Badge>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ══ ALL ATTENDANCE TAB ════════════════════════════════════════════════ */}
        <TabsContent value="list" className="flex-1 flex flex-col min-h-0 mt-0">
          <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0 md:mx-0 rounded-lg">

            {/* Desktop table */}
            <div className="hidden md:block overflow-auto flex-1">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-8 text-[10px] font-mono uppercase tracking-widest">#</TableHead>
                    <TableHead>{isStaff ? "Staff Member" : "Member"}</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>{groupByDay ? "First In" : "Check-In"}</TableHead>
                    <TableHead>{groupByDay ? "Last Out" : "Check-Out"}</TableHead>
                    <TableHead>{groupByDay ? "Total Time" : (isStaff ? "Shift Duration" : "Duration")}</TableHead>
                    {groupByDay && <TableHead className="w-28 text-center">Sessions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listLoading ? (
                    <TableRow>
                      <TableCell colSpan={groupByDay ? 7 : 6} className="text-center py-12">
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                          <span className="text-muted-foreground text-sm">Loading...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : listData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={groupByDay ? 7 : 6} className="text-center py-12 text-muted-foreground">No attendance records found</TableCell>
                    </TableRow>
                  ) : listData.map((r, idx) => {
                    const duration = r.totalDuration || r.duration || calcDuration(r.firstCheckIn || r.checkIn, r.lastCheckOut || r.checkOut);
                    const personName = isStaff ? (r.staff?.name || r.staffName || "Staff") : (r.member?.name || "Unknown");
                    const personPhoto = isStaff ? r.staff?.photo : r.member?.photo;
                    const personPhone = isStaff ? (r.staff?.phone || r.staffPhone || r.staffEmail) : r.member?.phone;
                    const isBiometric = r.source?.startsWith("biometric_") || r.source === "biometric";
                    const hasMultiSessions = r.sessions && r.sessions.length > 1;
                    const isRowExpanded = expandedRowIds.has(r.id);

                    return (
                      <Fragment key={r.id}>
                        <TableRow className="hover:bg-secondary/30" data-testid={`attendance-row-${r.id}`}>
                          <TableCell className="text-[10px] font-mono text-muted-foreground">
                            {(listPage - 1) * listPageSize + idx + 1}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <ClickableAvatar
                                src={personPhoto}
                                name={personName}
                                className="h-7 w-7 shrink-0"
                                fallbackClassName="bg-primary/10 text-primary text-[10px] font-bold"
                              />
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-sm font-medium leading-none">
                                  {!isStaff && r.member?.memberNumber && (
                                    <span className="text-muted-foreground text-[10px] font-mono mr-1">#{r.member?.memberNumber}</span>
                                  )}
                                  {personName}
                                </p>
                                {isBiometric && (
                                  <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[9px] font-mono py-0 h-4 flex items-center gap-0.5">
                                    <Fingerprint className="h-2.5 w-2.5" />Bio
                                  </Badge>
                                )}
                                {personPhone && (
                                  <span className="text-[10px] text-muted-foreground font-mono">• {personPhone}</span>
                                )}
                                {!isStaff && (r.member?.id || r.memberId) && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 text-muted-foreground hover:text-primary shrink-0"
                                    onClick={() => navigate(`/members/${r.member?.id || r.memberId}`)}
                                    title="View Profile"
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                )}
                                {isStaff && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-4 w-4 text-muted-foreground hover:text-primary shrink-0"
                                    onClick={() => navigate(`/staff`)}
                                    title="View Staff"
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm font-mono">{r.date}</TableCell>
                          <TableCell className="text-sm font-mono text-emerald-500">{formatTime(r.firstCheckIn || r.checkIn)}</TableCell>
                          <TableCell className="text-sm font-mono">
                            {r.lastCheckOut || r.checkOut
                              ? <span className="text-amber-500">{formatTime(r.lastCheckOut || r.checkOut)}</span>
                              : <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px]">Active</Badge>
                            }
                          </TableCell>
                          <TableCell>
                            {duration
                              ? <Badge className="bg-secondary text-muted-foreground border-0 text-[10px] font-mono">{duration}</Badge>
                              : <span className="text-muted-foreground text-xs">—</span>
                            }
                          </TableCell>
                          {groupByDay && (
                            <TableCell className="text-center">
                              {hasMultiSessions ? (
                                <button
                                  type="button"
                                  onClick={() => toggleRowExpanded(r.id)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-mono transition-colors"
                                  title="View session punches"
                                >
                                  <Layers className="h-3 w-3" />
                                  {r.sessionCount || r.sessions.length} sessions
                                  {isRowExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                </button>
                              ) : (
                                <span className="text-[10px] font-mono text-muted-foreground">1 session</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                        {groupByDay && hasMultiSessions && isRowExpanded && (
                          <TableRow className="bg-secondary/15 hover:bg-secondary/20 border-b border-border">
                            <TableCell colSpan={7} className="py-2.5 px-8">
                              <div className="bg-card/90 border border-border/70 rounded-md p-3 max-w-2xl shadow-xs">
                                <div className="flex items-center justify-between text-xs font-mono font-medium text-muted-foreground mb-2">
                                  <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-primary" />Detailed Sessions for {personName} ({r.date})</span>
                                  <span className="text-[11px]">Total Active Time: <strong className="text-foreground">{duration}</strong></span>
                                </div>
                                <div className="divide-y divide-border/40 text-xs font-mono">
                                  {r.sessions.map((s, sIdx) => (
                                    <div key={s.id || sIdx} className="flex items-center justify-between py-1.5 px-1">
                                      <span className="text-muted-foreground w-20 text-[11px]">Session #{sIdx + 1}</span>
                                      <div className="flex items-center gap-2">
                                        <span className="text-emerald-500 font-semibold">{formatTime(s.checkIn)}</span>
                                        <span className="text-muted-foreground">→</span>
                                        {s.checkOut ? <span className="text-amber-500 font-semibold">{formatTime(s.checkOut)}</span> : <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px] py-0">Active Inside</Badge>}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {s.duration && <Badge className="bg-secondary text-muted-foreground border-0 text-[10px]">{s.duration}</Badge>}
                                        {s.source === "biometric" && (
                                          <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[9px] font-mono py-0 flex items-center gap-0.5">
                                            <Fingerprint className="h-2.5 w-2.5" />{s.deviceName || "Bio"}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile list */}
            <div className="md:hidden overflow-y-auto flex-1 divide-y divide-border">
              {listLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                  <span className="text-muted-foreground text-sm ml-2">Loading...</span>
                </div>
              ) : listData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">No attendance records found</div>
              ) : listData.map((r) => {
                const duration = r.totalDuration || r.duration || calcDuration(r.firstCheckIn || r.checkIn, r.lastCheckOut || r.checkOut);
                const personName = isStaff ? (r.staff?.name || r.staffName || "Staff") : (r.member?.name || "Unknown");
                const personPhoto = isStaff ? r.staff?.photo : r.member?.photo;
                const personPhone = isStaff ? (r.staff?.phone || r.staffPhone || r.staffEmail) : r.member?.phone;
                const isBiometric = r.source?.startsWith("biometric_") || r.source === "biometric";
                const hasMultiSessions = r.sessions && r.sessions.length > 1;
                const isRowExpanded = expandedRowIds.has(r.id);

                return (
                  <div key={r.id} className="p-3 hover:bg-secondary/10">
                    <div className="flex items-center gap-2.5">
                      <ClickableAvatar
                        src={personPhoto}
                        name={personName}
                        className="h-8 w-8 shrink-0"
                        fallbackClassName="bg-primary/10 text-primary text-xs font-bold"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-medium truncate">
                            {!isStaff && r.member?.memberNumber && (
                              <span className="text-muted-foreground text-[10px] font-mono mr-1">#{r.member?.memberNumber}</span>
                            )}
                            {personName}
                          </p>
                          {isBiometric && (
                            <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[9px] font-mono py-0 h-4 flex items-center gap-0.5">
                              <Fingerprint className="h-2.5 w-2.5" />Bio
                            </Badge>
                          )}
                          {!isStaff && (r.member?.id || r.memberId) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 text-muted-foreground hover:text-primary shrink-0"
                              onClick={() => navigate(`/members/${r.member?.id || r.memberId}`)}
                              title="View Profile"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          )}
                          {isStaff && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 text-muted-foreground hover:text-primary shrink-0"
                              onClick={() => navigate(`/staff`)}
                              title="View Staff"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-[10px] text-muted-foreground font-mono">{r.date}</p>
                          {groupByDay && hasMultiSessions && (
                            <button
                              type="button"
                              onClick={() => toggleRowExpanded(r.id)}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded bg-primary/10 text-primary text-[9px] font-mono"
                            >
                              <Layers className="h-2.5 w-2.5" />
                              {r.sessionCount || r.sessions.length} sessions
                              {isRowExpanded ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-right space-y-0.5">
                        <div className="flex items-center gap-1 justify-end text-[10px] font-mono">
                          <span className="text-emerald-500">{formatTime(r.firstCheckIn || r.checkIn)}</span>
                          <span className="text-muted-foreground">→</span>
                          {r.lastCheckOut || r.checkOut
                            ? <span className="text-amber-500">{formatTime(r.lastCheckOut || r.checkOut)}</span>
                            : <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[9px]">Active</Badge>
                          }
                        </div>
                        {duration && (
                          <Badge className="bg-secondary text-muted-foreground border-0 text-[9px] font-mono float-right">{duration}</Badge>
                        )}
                      </div>
                    </div>

                    {/* Mobile Expanded Sessions */}
                    {groupByDay && hasMultiSessions && isRowExpanded && (
                      <div className="mt-2 ml-10 p-2 bg-secondary/20 rounded border border-border/40 space-y-1">
                        <div className="text-[10px] font-mono text-muted-foreground uppercase flex items-center justify-between">
                          <span>Sessions</span>
                          <span>Total: {duration}</span>
                        </div>
                        {r.sessions.map((s, sIdx) => (
                          <div key={s.id || sIdx} className="flex items-center justify-between text-[10px] font-mono py-0.5">
                            <span className="text-muted-foreground">#{sIdx + 1}</span>
                            <span className="text-emerald-500">{formatTime(s.checkIn)}</span>
                            <span>→</span>
                            {s.checkOut ? <span className="text-amber-500">{formatTime(s.checkOut)}</span> : <span className="text-emerald-400 font-bold">Active</span>}
                            <span className="text-muted-foreground">{s.duration || "—"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="flex flex-row items-center justify-between gap-4 p-3 border-t border-border shrink-0">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className="font-mono hidden sm:block">{listTotal} total</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs">Show:</span>
                  <Select value={String(listPageSize)} onValueChange={(v) => { setListPageSize(Number(v)); setListPage(1); }}>
                    <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={listPage === 1} onClick={() => setListPage(p => p - 1)} data-testid="prev-page-btn">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="sm:text-sm text-xs text-muted-foreground px-1">Page {listPage} of {totalListPages}</span>
                <Button variant="outline" size="sm" disabled={listPage >= totalListPages} onClick={() => setListPage(p => p + 1)} data-testid="next-page-btn">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Quick Check-in Dialog ── */}
      <QuickCheckinDialog
        open={showCheckinDialog}
        onOpenChange={setShowCheckinDialog}
        branchId={currentBranch?.id}
        checkedInIds={checkedInIds}
        onCheckin={handleCheckin}
        onCheckout={handleCheckout}
        attendanceType={attendanceType}
      />

      {/* ── Filters Drawer ── */}
      <FiltersDrawer
        open={showFilters}
        onOpenChange={setShowFilters}
        filters={filters}
        onChange={(f) => { setFilters(f); setListPage(1); }}
        onClear={() => { setFilters(EMPTY_FILTERS); setListPage(1); }}
        attendanceType={attendanceType}
      />
    </div>
  );
}
